import type { SercAgenciesQuery } from "generated-graphql/graphql";

import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import { Box, Button, Tooltip } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { STATE_NAMES } from "encamp-shared/src/constants/states";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import { useMemo, useState } from "react";

import { ConfirmDeleteSercAgency } from "./ConfirmDeleteSercAgency";
import { GET_SERC_AGENCIES } from "./schema";
import { SercAgencyDialog } from "./SercAgencyDialog";

type Row = SercAgenciesQuery["sercAgencies"]["items"][number];

type SercsProps = {
  initialPageSize?: number;
};

export function Sercs({ initialPageSize }: SercsProps) {
  const {
    selectedId: sercAgencyId,
    pushIdToUrl,
    popIdFromUrl,
  } = useSelectedIdInUrl("sercAgencyId");
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [confirmDeleteSerc, setConfirmDeleteSerc] = useState<Row | null>(null);

  const columns: OmnisearchGridColDef<Row>[] = useMemo(
    () => [
      {
        field: "state",
        headerName: "State/Territory",
        flex: 1,
        valueFormatter: (value) => {
          const longValue = STATE_NAMES[value];
          if (longValue) {
            return `${value} (${longValue})`;
          }
          return value;
        },
      },
      { field: "email", headerName: "Contact Email", flex: 4 },
      { field: "recipient", headerName: "Recipient", flex: 4 },
      {
        field: "submitsViaEmail",
        headerName: "Email",
        filterKeyType: "boolean",
        type: "boolean",
        flex: 1,
      },
      {
        field: "submitsViaPortal",
        headerName: "Portal",
        filterKeyType: "boolean",
        type: "boolean",
        flex: 1,
      },
      {
        field: "submitsViaForm",
        headerName: "Form",
        filterKeyType: "boolean",
        type: "boolean",
        flex: 1,
      },
      {
        field: "actions",
        type: "actions",
        align: "left",
        getActions: ({ row }) => [
          <Tooltip title="Edit SERC" key="edit">
            <GridActionsCellItem
              label="Edit SERC"
              onClick={() => pushIdToUrl(row.id)}
              icon={<Edit />}
            />
          </Tooltip>,
          <Tooltip title="Delete SERC" key="delete">
            <GridActionsCellItem
              label="Delete SERC"
              onClick={() => setConfirmDeleteSerc(row)}
              icon={<Delete />}
            />
          </Tooltip>,
        ],
      },
    ],
    [pushIdToUrl]
  );

  return (
    <Box sx={{ pt: 3 }}>
      <OmnisearchDataGrid
        persistenceKey="staff-sercs"
        isURLDriven
        columns={columns}
        dataQuery={GET_SERC_AGENCIES}
        getItems={(data) => data.sercAgencies.items ?? []}
        getCount={(data) => data.sercAgencies.count ?? 0}
        noDataMessage="No SERC agencies found."
        initialPageSize={initialPageSize ?? 25}
        filterGroups={[
          {
            name: "State",
            fields: ["state"],
          },
          {
            name: "Contact Email",
            fields: ["email"],
          },
          {
            name: "Submission Methods",
            fields: ["submitsViaEmail", "submitsViaPortal", "submitsViaForm"],
          },
        ]}
        onRowClick={({ row }) => pushIdToUrl(row.id)}
        commandButtons={[
          <Button key="add-serc" variant="contained" onClick={() => setAddDialogOpen(true)}>
            Add SERC Agency
          </Button>,
        ]}
      />
      {sercAgencyId && (
        <SercAgencyDialog onClose={popIdFromUrl} mode="edit" sercAgencyId={sercAgencyId} />
      )}
      {addDialogOpen && <SercAgencyDialog onClose={() => setAddDialogOpen(false)} mode="add" />}
      {confirmDeleteSerc !== null && (
        <ConfirmDeleteSercAgency
          sercAgencyId={confirmDeleteSerc.id}
          onClose={() => setConfirmDeleteSerc(null)}
        />
      )}
    </Box>
  );
}
